import PropTypes from 'prop-types';

import {
  Box, Flex, Heading, SimpleGrid, Skeleton, Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogSummary} from '@amblique/king-living/app/components/contentful/blog-summary';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {useResolvedInlinedBlogGridCtaBlogSysId} from '@amblique/king-living/app/hooks/use-resolved-inline-blog-grid-cta-sys-id';

/**
 * Repeating row pattern for Blog Listing layout:
 * - Even index rows (0, 2, 4...): 2 items with alternating column layouts
 * - Odd index rows (1, 3, 5...): 3 items equal
 *
 * The 2-item rows alternate between 'twoColLeft' (65/35) and 'twoColRight' (35/65)
 * using className toggle for easier styling management.
 */
const ROW_PATTERN = [
  {type: 'twoColLeft', count: 2},
  {type: 'threeCol', count: 3},
  {type: 'twoColRight', count: 2},
  {type: 'threeCol', count: 3}
];

/**
 * Split blogs into rows based on the repeating pattern
 * @param {Array} blogs - Array of blog items
 * @returns {Array} - Array of row objects with type and items
 */
const splitIntoRows = (blogs) => {
  const rows = [];
  let currentIndex = 0;
  let patternIndex = 0;

  while (currentIndex < blogs.length) {
    const pattern = ROW_PATTERN[patternIndex % ROW_PATTERN.length];
    const rowItems = blogs.slice(currentIndex, currentIndex + pattern.count);

    if (rowItems.length > 0) {
      rows.push({
        type: pattern.type,
        items: rowItems,
      });
    }

    currentIndex += pattern.count;
    patternIndex += 1;
  }

  return rows;
};

/**
 * BlogGridListing - Enhanced blog grid with repeating row pattern
 * Used for Blog Listing pages (King Journal)
 *
 * Row pattern (repeating):
 * - Row 0: 2 items (65% left, 35% right) - twoColLeft
 * - Row 1: 3 items equal - threeCol
 * - Row 2: 2 items (35% left, 65% right) - twoColRight
 * - Row 3: 3 items equal - threeCol
 * - Row 4: repeat from Row 0...
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - BlogGridListing component data
 * @param {string} [props.component.heading] - Section heading (storefront)
 * @param {string} [props.component.title] - Entry title (CMS only, not rendered)
 * @param {string} props.component.description - Section description
 * @param {Object[]} props.component.blogSummaryCollection - Collection of blog summaries
 * @param {Object} props.component.cta - CTA link component
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {Object} props.styles - Component styles
 * @returns {React.ReactElement}
 */
export const BlogGridListing = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {description, blogSummaryCollection, cta} = component;
  const headingText = typeof component?.heading === 'string' ? component.heading.trim() : '';
  const blogSummaries = (blogSummaryCollection?.items || []).filter(Boolean);

  const inlinedBlogGridCtaBlogSysId = useResolvedInlinedBlogGridCtaBlogSysId(cta, blogSummaries);

  // Split blogs into rows based on repeating pattern
  const rows = splitIntoRows(blogSummaries);

  return (
    <Box {...styles.container} className="blog-grid-listing blog-grid__container">
      {(headingText || description) && (
        <Flex
          {...styles.headerRow}
          className={
            ['blog-grid__header', 'blog-grid-listing__header', styles.headerRow?.className].filter(Boolean).join(' ')
          }
        >
          <Box {...styles.headingRegion}>
            {headingText ? <Heading {...styles.title}>{headingText}</Heading> : null}
          </Box>
          {description ? <Text {...styles.description}>{description}</Text> : null}
        </Flex>
      )}

      {/* Dynamic Rows */}
      {!isComplete && isFetching ? (
        <Skeleton height="600px" width="100%" />
      ) : (
        rows.map((row, rowIndex) => {
          // 2-column row with alternating layouts (twoColLeft or twoColRight)
          if (row.type === 'twoColLeft' || row.type === 'twoColRight') {
            return (
              <Flex
                key={`row-${rowIndex}`}
                className={`blog-grid-listing__row--${row.type}`}
                {...styles.twoColRow}
                data-layout={row.type}
              >
                {row.items.map((blog, index) => {
                  // For twoColRight: item 0 = small (35%), item 1 = large (65%)
                  // For twoColLeft: item 0 = large (65%), item 1 = small (35%)
                  const isLarge = row.type === 'twoColRight' ? index === 1 : index === 0;
                  const sizeClass = isLarge ? 'large' : 'small';

                  return (
                    <Box
                      key={blog.sys?.id || `${rowIndex}-${index}`}
                      className={`blog-grid-listing__item--${sizeClass}`}
                      {...(isLarge ? styles.twoColLarge : styles.twoColSmall)}
                      data-position={sizeClass}
                      data-row-type={row.type}
                    >
                      <Component
                        as={BlogSummary}
                        component={blog}
                        gridSectionCta={
                          inlinedBlogGridCtaBlogSysId &&
                          blog?.sys?.id &&
                          inlinedBlogGridCtaBlogSysId === blog.sys.id
                            ? cta
                            : undefined
                        }
                        isComplete={isComplete}
                        isFetching={isFetching}
                        slug={blog.pageSlug}
                      />
                    </Box>
                  );
                })}
              </Flex>
            );
          }

          // 3-column row (equal)
          return (
            <SimpleGrid
              key={`row-${rowIndex}`}
              {...styles.threeColRow}
              className="blog-grid__posts blog-grid-listing__row--three-col"
            >
              {row.items.map((blog, index) => (
                <Component
                  key={blog.sys?.id || `${rowIndex}-${index}`}
                  as={BlogSummary}
                  component={blog}
                  gridSectionCta={
                    inlinedBlogGridCtaBlogSysId &&
                    blog?.sys?.id &&
                    inlinedBlogGridCtaBlogSysId === blog.sys.id
                      ? cta
                      : undefined
                  }
                  isComplete={isComplete}
                  isFetching={isFetching}
                  slug={blog.pageSlug}
                />
              ))}
            </SimpleGrid>
          );
        })
      )}

      {/* CTA Link (section-level; theme: contentful/blog-grid or contentful/blog-listing → ctaContainer) */}
      {cta && !inlinedBlogGridCtaBlogSysId ? (
        <Box
          {...styles.ctaContainer}
          className={
            ['blog-grid-listing__cta', styles.ctaContainer?.className].filter(Boolean).join(' ')
          }
        >
          <ContentfulLink component={cta} isComplete={isComplete} isFetching={isFetching} />
        </Box>
      ) : null}
    </Box>
  );
};

BlogGridListing.displayName = 'BlogGridListing';
BlogGridListing.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
